import type { OrderStatus } from '@palihog/shared';

const EARLY_CANCELLABLE_STATES = new Set<OrderStatus>([
  'submitted',
  'under_review',
  'awaiting_customer_approval',
  'confirmed',
]);

export interface AdminCancellationAvailability {
  eligible: boolean;
  reason: string | null;
}

export function adminCancellationAvailability(status: OrderStatus): AdminCancellationAvailability {
  if (EARLY_CANCELLABLE_STATES.has(status)) return { eligible: true, reason: null };
  if (status === 'cancelled') return { eligible: false, reason: 'This order is already cancelled.' };
  if (status === 'rejected') return { eligible: false, reason: 'This order was rejected and is already closed.' };
  if (status === 'completed') return { eligible: false, reason: 'Completed orders require financial reconciliation, not cancellation.' };
  if (status === 'delivery_failed') return { eligible: false, reason: 'Failed deliveries require a documented resolution workflow.' };
  return { eligible: false, reason: 'Active deliveries require a separate assignment and financial resolution workflow.' };
}

export function adminCancellationErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (message.includes('Order changed') || message.includes('Only unassigned')) {
    return 'The order state changed and is no longer eligible for this cancellation. Refresh the queue and review its current state.';
  }
  if (message.includes('rider assignment history')) {
    return 'This order has rider assignment history and must use the separate resolution workflow.';
  }
  if (message.includes('active dispatch state')) {
    return 'A dispatch attempt or rider offer is active. Wait for it to resolve, then refresh before trying again.';
  }
  if (message.includes('financial activity') || message.includes('purchase receipt activity')) {
    return 'This order has financial or purchase activity and must be reconciled before any closure action.';
  }
  if (message.includes('Admin required') || message.includes('permission denied')) {
    return 'Your session is not authorized to cancel orders. Sign in again with an administrator account.';
  }
  return message || 'The cancellation could not be completed. Refresh and try again.';
}
